import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const SHORT_ACTIVATE_DELAY = 0;
const LONG_ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#desktopMenuQuery.addEventListener('change', this.#syncDesktopOverflow, {
      signal: this.#abortController.signal,
    });
    this.#syncDesktopOverflow(this.#desktopMenuQuery);

    this.addEventListener('keydown', this.#handleKeydown, {
      signal: this.#abortController.signal,
    });
    this.addEventListener('pointerover', this.#handleMegaCategoryInteraction, {
      signal: this.#abortController.signal,
    });
    this.addEventListener('focusin', this.#handleMegaCategoryInteraction, {
      signal: this.#abortController.signal,
    });
    this.addEventListener('pointerout', this.#handleMegaCategoryPointerout, {
      signal: this.#abortController.signal,
    });
    this.addEventListener('click', this.#handleMegaCategoryInteraction, {
      signal: this.#abortController.signal,
    });
    this.addEventListener('scroll', this.#handleProductAreaScroll, {
      capture: true,
      passive: true,
      signal: this.#abortController.signal,
    });

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    onDocumentReady(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#subcategoryHoverTimer);
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    if (this.#state.activeItem) {
      this.#shortDebouncedActivateHandler(event);
    } else {
      this.#longDebouncedActivateHandler(event);
    }
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    this.#loadSubmenuImages(submenu);
    this.#ensureActiveSubcategory(submenu);

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
  };

  #shortDebouncedActivateHandler = debounce(this.#activateHandler, SHORT_ACTIVATE_DELAY);
  #longDebouncedActivateHandler = debounce(this.#activateHandler, LONG_ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered && !force) return;

    this.style.setProperty('--submenu-height', '0px');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  #handleKeydown = (event) => {
    const categoryTab = event.target instanceof Element ? event.target.closest('[data-mega-collection-url]') : null;
    if (categoryTab && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      const tabs = [...categoryTab.closest('.bodega-mega-menu__category-list')?.querySelectorAll('[data-mega-collection-url]') ?? []];
      const currentIndex = tabs.indexOf(categoryTab);
      if (currentIndex < 0) return;

      event.preventDefault();
      let nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : currentIndex;
      if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      tabs[nextIndex]?.focus();
      return;
    }

    if (event.key !== 'Escape' || !this.#state.activeItem) return;

    event.preventDefault();
    const activeItem = this.#state.activeItem;
    this.#deactivate(activeItem, true);
    activeItem.focus();
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]:not([data-mega-menu-thumbnail])');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #loadSubmenuImages = (submenu) => {
    if (!submenu) return;

    const images = submenu.querySelectorAll('img[data-mega-menu-thumbnail][data-src]');
    images.forEach((image) => {
      if (!(image instanceof HTMLImageElement)) return;

      image.src = image.dataset.src ?? '';
      if (image.dataset.srcset) image.srcset = image.dataset.srcset;
      image.removeAttribute('data-src');
      image.removeAttribute('data-srcset');
    });
  };

  #collectionCache = new Map();
  #subcategoryHoverTimer;

  #handleMegaCategoryInteraction = (event) => {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[data-mega-collection-url]');
    if (!(tab instanceof HTMLButtonElement)) return;

    const panel = tab.closest('.bodega-mega-menu')?.querySelector('[data-mega-products-panel]');
    if (!(panel instanceof HTMLElement)) return;

    if (event.type === 'pointerover') {
      clearTimeout(this.#subcategoryHoverTimer);
      this.#subcategoryHoverTimer = setTimeout(() => this.#selectSubcategory(tab, panel), 140);
      return;
    }

    clearTimeout(this.#subcategoryHoverTimer);
    this.#selectSubcategory(tab, panel);
  };

  #handleMegaCategoryPointerout = (event) => {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[data-mega-collection-url]');
    if (!tab || (event.relatedTarget instanceof Node && tab.contains(event.relatedTarget))) return;
    clearTimeout(this.#subcategoryHoverTimer);
  };

  #ensureActiveSubcategory = (submenu) => {
    if (!(submenu instanceof HTMLElement)) return;
    const panel = submenu.querySelector('[data-mega-products-panel]');
    const tab = submenu.querySelector('[data-mega-collection-url][aria-pressed="true"]');
    if (panel instanceof HTMLElement && tab instanceof HTMLButtonElement && !panel.dataset.megaLoadedUrl) {
      this.#selectSubcategory(tab, panel);
    }
  };

  #selectSubcategory = async (tab, panel) => {
    const collectionUrl = tab.dataset.megaCollectionUrl;
    if (!collectionUrl) return;

    const tablist = tab.closest('.bodega-mega-menu__category-list');
    tablist?.querySelectorAll('[data-mega-collection-url]').forEach((item) => {
      const isSelected = item === tab;
      item.setAttribute('aria-pressed', isSelected.toString());
      item.setAttribute('tabindex', isSelected ? '0' : '-1');
      item.classList.toggle('is-active', isSelected);
    });

    panel.setAttribute('aria-labelledby', tab.id);
    panel.dataset.megaActiveUrl = collectionUrl;

    const cached = this.#collectionCache.get(collectionUrl);
    if (cached) {
      panel.innerHTML = cached;
      panel.dataset.megaLoadedUrl = collectionUrl;
      this.#syncSubmenuHeight(panel);
      return;
    }

    panel.innerHTML = '<p class="bodega-mega-menu__status" role="status">Cargando productos…</p>';
    await this.#loadCollectionPage(panel, collectionUrl, 1, false);
  };

  #loadCollectionPage = async (panel, collectionUrl, page, append) => {
    const requestKey = `${collectionUrl}:${page}`;
    if (panel.dataset.megaLoading === requestKey) return;
    panel.dataset.megaLoading = requestKey;

    try {
      const endpoint = new URL(collectionUrl, window.location.origin);
      endpoint.searchParams.set('view', 'mega-menu');
      endpoint.searchParams.set('page', page.toString());
      const response = await fetch(endpoint, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Mega menu request failed: ${response.status}`);

      const html = await response.text();
      if (panel.dataset.megaActiveUrl !== collectionUrl) return;

      if (append) panel.insertAdjacentHTML('beforeend', html);
      else panel.innerHTML = html;

      panel.dataset.megaLoadedUrl = collectionUrl;
      this.#collectionCache.set(collectionUrl, panel.innerHTML);
      this.#syncSubmenuHeight(panel);
    } catch (error) {
      if (panel.dataset.megaActiveUrl === collectionUrl) {
        panel.innerHTML = '<p class="bodega-mega-menu__status" role="status">No fue posible cargar los productos. </p>';
        const fallbackLink = document.createElement('a');
        fallbackLink.href = collectionUrl;
        fallbackLink.textContent = 'Ver la colección';
        panel.querySelector('.bodega-mega-menu__status')?.append(fallbackLink);
        this.#syncSubmenuHeight(panel);
      }
      console.warn(error);
    } finally {
      if (panel.dataset.megaLoading === requestKey) delete panel.dataset.megaLoading;
    }
  };

  #handleProductAreaScroll = (event) => {
    const panel = event.target;
    if (!(panel instanceof HTMLElement) || !panel.matches('[data-mega-products-panel]')) return;
    if (panel.scrollHeight - panel.scrollTop - panel.clientHeight > 120) return;

    const lastPage = panel.querySelector('[data-mega-products-page]:last-of-type');
    const nextPage = lastPage?.dataset.megaNextPage;
    const collectionUrl = panel.dataset.megaActiveUrl;
    if (nextPage && collectionUrl) this.#loadCollectionPage(panel, collectionUrl, Number(nextPage), true);
  };

  #syncSubmenuHeight = (panel) => {
    const submenu = panel.closest('[ref="submenu[]"]');
    if (!(submenu instanceof HTMLElement)) return;
    const overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;
    this.style.setProperty('--submenu-height', `${Math.max(submenu.offsetHeight, overflowMenuHeight)}px`);
  };

  #desktopMenuQuery = window.matchMedia('(min-width: 1200px)');

  #syncDesktopOverflow = ({ matches }) => {
    if (matches) {
      this.refs.overflowMenu?.setAttribute('disabled', 'true');
    } else {
      this.refs.overflowMenu?.removeAttribute('disabled');
    }
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[role="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
